// /admin/src/pages/TutorApplications.js
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { tutorService } from '../services/tutorService';
import socketService from '../services/socketService'; // 🆕 Add this
import { formatDate } from '../utils/formatters';
import { 
  Users, 
  FileText, 
  Download, 
  CheckCircle, 
  XCircle, 
  Eye,
  Loader,
  AlertCircle,
  Clock,
  GraduationCap,
  FileCheck,
  BookOpen,
  UserCheck,
  UserX,
  Wifi,
  WifiOff,
  Bell,
  RefreshCw
} from 'lucide-react';

// Modern Toast Notification Component
const Toast = ({ message, type = 'success', onClose }) => {
  const bgColor = type === 'success' ? 'bg-green-50 border-green-200' : 
                   type === 'error' ? 'bg-red-50 border-red-200' :
                   type === 'info' ? 'bg-blue-50 border-blue-200' :
                   'bg-gray-50 border-gray-200';
  const textColor = type === 'success' ? 'text-green-800' : 
                   type === 'error' ? 'text-red-800' :
                   type === 'info' ? 'text-blue-800' :
                   'text-gray-800';
  const Icon = type === 'success' ? UserCheck : 
               type === 'error' ? UserX :
               type === 'info' ? Bell :
               AlertCircle;

  return (
    <div className={`fixed top-4 right-4 ${bgColor} border rounded-lg p-4 shadow-lg z-50 max-w-sm animate-slide-in`}>
      <div className="flex items-start space-x-3">
        <Icon className={`h-5 w-5 ${textColor} mt-0.5 flex-shrink-0`} />
        <div className="flex-1">
          <p className={`text-sm font-medium ${textColor}`}>{message}</p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <XCircle className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Modern Confirmation Modal
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, confirmColor }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600 mb-6">{message}</p>
          
          <div className="flex space-x-3 justify-center">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 ${confirmColor} text-white rounded-lg hover:opacity-90 transition-opacity font-medium`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ApplicationCard = ({ application, onApprove, onReject, loading }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const handleApproveClick = () => {
    setShowApproveModal(true);
  };

  const handleRejectClick = () => {
    if (!rejectReason.trim()) {
      setShowRejectForm(true);
      return;
    }
    setShowRejectModal(true);
  };

  const handleApproveConfirm = () => {
    onApprove(application.id);
    setShowApproveModal(false);
  };

  const handleRejectConfirm = () => {
    onReject(application.id, rejectReason);
    setShowRejectModal(false);
    setShowRejectForm(false);
    setRejectReason('');
  };

  // Map your backend fields to the frontend
  const essay = application.application_essay || application.essay || 'No essay provided';
  const certificateUrl = application.certificate_url;
  const appliedDate = application.applied_at || application.created_at;
  const wordCount = essay ? essay.split(/\s+/).filter(word => word.length > 0).length : 0;

  return (
    <>
      <div className="card border-l-4 border-l-blue-500 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-start space-x-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl shadow-sm">
                <GraduationCap className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{application.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{application.email}</p>
                    {application.education && (
                      <p className="text-sm text-blue-600 font-medium mt-2">
                        🎓 {application.education}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                      <Clock className="h-3 w-3 mr-1" />
                      Pending Review
                    </span>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <span className="font-medium text-gray-500 block mb-1">Applied Date</span>
                    <p className="text-gray-900 font-semibold">{formatDate(appliedDate)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <span className="font-medium text-gray-500 block mb-1">Application Status</span>
                    <p className="text-yellow-700 font-semibold">Awaiting Review</p>
                  </div>
                </div>

                {showDetails && (
                  <div className="mt-6 space-y-6 border-t pt-6">
                    {/* Enhanced Essay Section */}
                    <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-5 border border-gray-200">
                      <div className="flex items-center space-x-2 mb-4">
                        <BookOpen className="h-5 w-5 text-blue-600" />
                        <h4 className="text-lg font-semibold text-gray-900">Application Essay</h4>
                      </div>
                      <div className="bg-white rounded-lg p-4 max-h-48 overflow-y-auto shadow-inner border">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-line text-sm">
                          {essay}
                        </p>
                      </div>
                      <div className="flex justify-between items-center mt-3">
                        <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full border">
                          📝 {wordCount} words
                        </span>
                        <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full border">
                          ⏱️ {Math.ceil(wordCount / 200)} min read
                        </span>
                      </div>
                    </div>

                    {/* Enhanced Certificate Section */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
                      <div className="flex items-center space-x-2 mb-4">
                        <FileCheck className="h-5 w-5 text-green-600" />
                        <h4 className="text-lg font-semibold text-gray-900">Academic Certificate</h4>
                      </div>
                      {certificateUrl ? (
                        <div className="flex items-center justify-between bg-white rounded-lg p-4 border">
                          <div className="flex items-center space-x-3">
                            <div className="bg-green-100 p-2 rounded-lg">
                              <FileText className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Certificate File</p>
                              <p className="text-xs text-gray-500">Ready for review</p>
                            </div>
                          </div>
                          <button 
                            onClick={() => window.open(`http://localhost:5000${certificateUrl}`, '_blank')}
                            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                          >
                            <Download className="h-4 w-4" />
                            <span className="text-sm font-medium">View Certificate</span>
                          </button>
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-white rounded-lg border border-dashed border-gray-300">
                          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                          <p className="text-gray-500 font-medium">No certificate uploaded</p>
                          <p className="text-sm text-gray-400 mt-1">Applicant did not provide a certificate</p>
                        </div>
                      )}
                    </div>

                    {/* Enhanced Additional Info */}
                    {application.experience && (
                      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-5 border border-purple-200">
                        <div className="flex items-center space-x-2 mb-4">
                          <UserCheck className="h-5 w-5 text-purple-600" />
                          <h4 className="text-lg font-semibold text-gray-900">Professional Experience</h4>
                        </div>
                        <div className="bg-white rounded-lg p-4 border">
                          <p className="text-gray-700 leading-relaxed">{application.experience}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-6 flex items-center space-x-4">
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                    <span>{showDetails ? 'Hide Details' : 'View Full Application'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Action Buttons */}
          <div className="flex flex-col space-y-3 ml-6">
            <button
              onClick={handleApproveClick}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
            >
              {loading ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              <span className="font-medium">Approve</span>
            </button>

            {!showRejectForm ? (
              <button
                onClick={() => setShowRejectForm(true)}
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <XCircle className="h-4 w-4" />
                <span className="font-medium">Reject</span>
              </button>
            ) : (
              <div className="space-y-3 bg-red-50 rounded-xl p-4 border border-red-200">
                <div>
                  <label className="block text-sm font-medium text-red-700 mb-2">
                    Reason for Rejection *
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Please provide a clear reason for rejecting this application..."
                    className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows="3"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleRejectClick}
                    disabled={loading || !rejectReason.trim()}
                    className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors font-medium"
                  >
                    Confirm Rejection
                  </button>
                  <button
                    onClick={() => {
                      setShowRejectForm(false);
                      setRejectReason('');
                    }}
                    className="flex-1 px-3 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <ConfirmationModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={handleApproveConfirm}
        title="Approve Tutor Application"
        message={`Are you sure you want to approve ${application.name}'s tutor application? This action cannot be undone.`}
        confirmText="Yes, Approve"
        confirmColor="bg-green-600 hover:bg-green-700"
      />

      <ConfirmationModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={handleRejectConfirm}
        title="Reject Tutor Application"
        message={`Are you sure you want to reject ${application.name}'s tutor application? This action cannot be undone.`}
        confirmText="Yes, Reject"
        confirmColor="bg-red-600 hover:bg-red-700"
      />
    </>
  );
};

const TutorApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [adminId, setAdminId] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [onlineAdmins, setOnlineAdmins] = useState(0);

  // 🆕 Initialize WebSocket connection
  useEffect(() => {
    const initializeSocket = async () => {
      try {
        // Get admin ID from local storage or context
        const adminData = localStorage.getItem('admin');
        if (adminData) {
          const admin = JSON.parse(adminData);
          setAdminId(admin.id);
          
          // Get token
          const token = localStorage.getItem('adminToken');
          
          // Connect to WebSocket
          socketService.connect(admin.id, token);
          
          // Set up socket event handlers
          socketService.on('admin-authenticated', () => {
            setSocketConnected(true);
            showToast('Connected to real-time notifications', 'info');
          });

          socketService.on('admin-notification', (notification) => {
            console.log('👑 Received admin notification:', notification);
            showToast(`New notification: ${notification.data?.tutorName || 'Tutor'} ${notification.type === 'tutor_approved' ? 'approved' : 'rejected'}`, 'info');
            
            // If this notification is about tutor approval/rejection, refresh the list
            if (notification.type === 'tutor_approved' || notification.type === 'tutor_rejected') {
              // Remove the processed application from the list
              setApplications(prev => prev.filter(app => app.id !== notification.data.tutorId));
            }
          });

          socketService.on('tutor-approved', (data) => {
            console.log('🎯 Real-time tutor approval:', data);
            showToast(`Tutor ${data.tutorName} approved by ${data.approvedBy}`, 'info');
          });

          socketService.on('tutor-rejected', (data) => {
            console.log('🎯 Real-time tutor rejection:', data);
            showToast(`Tutor ${data.tutorName} rejected by ${data.rejectedBy}`, 'info');
          });

          // Handle connection status
          socketService.on('connect', () => {
            setSocketConnected(true);
          });

          socketService.on('disconnect', () => {
            setSocketConnected(false);
            showToast('Disconnected from real-time notifications', 'error');
          });

          socketService.on('error', (error) => {
            console.error('Socket error:', error);
            showToast('Connection error: ' + error.message, 'error');
          });
          
          // Set up global handlers for other components
          window.refreshTutorApplications = fetchApplications;
          window.dispatchAdminNotification = (notification) => {
            showToast(`Admin Notification: ${notification.type}`, 'info');
          };
          
        }
      } catch (error) {
        console.error('Failed to initialize socket:', error);
        showToast('Failed to connect to real-time updates', 'error');
      }
    };

    initializeSocket();

    // Cleanup on unmount
    return () => {
      socketService.disconnect();
      socketService.offAll(); // Remove all event listeners
      window.refreshTutorApplications = null;
      window.dispatchAdminNotification = null;
    };
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await tutorService.getPendingApplications();
      
      if (response.success) {
        setApplications(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      console.error('Failed to fetch applications:', err);
      setError('Failed to load tutor applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleApprove = async (tutorId) => {
    setProcessing(prev => ({ ...prev, [tutorId]: 'approve' }));
    
    try {
      const response = await tutorService.approveApplication(tutorId);
      
      if (response.success) {
        // Remove from local state
        setApplications(prev => prev.filter(app => app.id !== tutorId));
        
        // 🆕 Send real-time notification via socket
        const tutor = applications.find(app => app.id === tutorId);
        if (tutor && socketConnected) {
          socketService.emit('tutor-approved', {
            tutorId,
            tutorName: tutor.name,
            approvedBy: adminId || 'Admin',
            timestamp: new Date()
          });
        }
        
        showToast('Tutor application approved successfully! Real-time update sent.', 'success');
      } else {
        showToast(`Failed to approve: ${response.message}`, 'error');
      }
    } catch (error) {
      console.error('Approve error:', error);
      showToast('Failed to approve application. Please try again.', 'error');
    } finally {
      setProcessing(prev => ({ ...prev, [tutorId]: false }));
    }
  };

  const handleReject = async (tutorId, reason) => {
    setProcessing(prev => ({ ...prev, [tutorId]: 'reject' }));
    
    try {
      console.log('🎯 Starting rejection process for tutor:', tutorId);
      console.log('📝 Rejection reason:', reason);

      const response = await tutorService.rejectApplication(tutorId, reason);
      
      if (response.success) {
        // Remove from local state
        setApplications(prev => prev.filter(app => app.id !== tutorId));
        
        // 🆕 Send real-time notification via socket
        const tutor = applications.find(app => app.id === tutorId);
        if (tutor && socketConnected) {
          socketService.emit('tutor-rejected', {
            tutorId,
            tutorName: tutor.name,
            reason: reason,
            rejectedBy: adminId || 'Admin',
            timestamp: new Date()
          });
        }
        
        showToast('Tutor application rejected successfully! Real-time update sent.', 'success');
      } else {
        showToast(response.message, 'error');
      }
    } catch (error) {
      console.error('💥 Rejection error:', error);
      showToast('An unexpected error occurred. Please try again.', 'error');
    } finally {
      setProcessing(prev => ({ ...prev, [tutorId]: false }));
    }
  };

  // 🆕 Function to manually refresh applications
  const handleManualRefresh = () => {
    fetchApplications();
    showToast('Applications refreshed', 'info');
  };

  // 🆕 Function to reconnect socket
  const handleReconnectSocket = () => {
    socketService.disconnect();
    setTimeout(() => {
      const adminData = localStorage.getItem('admin');
      if (adminData) {
        const admin = JSON.parse(adminData);
        const token = localStorage.getItem('adminToken');
        socketService.connect(admin.id, token);
        showToast('Attempting to reconnect...', 'info');
      }
    }, 1000);
  };

  return (
    <DashboardLayout>
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
      
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              Tutor Applications
            </h1>
            <p className="text-gray-600 mt-2 text-lg">Review and manage pending tutor applications</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* 🆕 Real-time Status Indicator */}
            <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${socketConnected ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {socketConnected ? (
                <>
                  <Wifi className="h-4 w-4" />
                  <span className="text-sm font-medium">Live Updates</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-4 w-4" />
                  <span className="text-sm font-medium">Offline</span>
                </>
              )}
            </div>
            
            {/* 🆕 Reconnect Button (only show when disconnected) */}
            {!socketConnected && (
              <button
                onClick={handleReconnectSocket}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                <RefreshCw className="h-4 w-4" />
                <span className="text-sm font-medium">Reconnect</span>
              </button>
            )}
            
            {/* Refresh Button */}
            <button
              onClick={handleManualRefresh}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="font-medium">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl mb-8">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 mr-3" />
            {error}
          </div>
        </div>
      )}

      {/* 🆕 Real-time Information Bar */}
      {socketConnected && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Wifi className="h-4 w-4" />
              <span className="font-medium">Real-time updates active</span>
              <span className="text-sm opacity-75">• Changes made by other admins will appear instantly</span>
            </div>
            <div className="flex items-center space-x-1 text-sm">
              <Bell className="h-4 w-4" />
              <span>Notifications enabled</span>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Statistics Bar */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center">
            <div className="bg-blue-600 p-3 rounded-xl">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-blue-600">Total Pending</p>
              <p className="text-2xl font-bold text-blue-700">{applications.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <div className="flex items-center">
            <div className="bg-yellow-600 p-3 rounded-xl">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-yellow-600">Awaiting Review</p>
              <p className="text-2xl font-bold text-yellow-700">{applications.length}</p>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center">
            <div className="bg-green-600 p-3 rounded-xl">
              <FileCheck className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-green-600">With Certificates</p>
              <p className="text-2xl font-bold text-green-700">
                {applications.filter(app => app.certificate_url).length}
              </p>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center">
            <div className="bg-purple-600 p-3 rounded-xl">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-purple-600">Essays Submitted</p>
              <p className="text-2xl font-bold text-purple-700">{applications.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Applications List */}
      <div className="space-y-6">
        {loading ? (
          <div className="card text-center py-16">
            <Loader className="h-12 w-12 animate-spin text-primary-600 mx-auto" />
            <p className="mt-4 text-gray-600 text-lg">Loading tutor applications...</p>
            <p className="text-sm text-gray-500 mt-2">Please wait while we fetch the latest applications</p>
          </div>
        ) : applications.length > 0 ? (
          applications.map((application) => (
            <ApplicationCard
              key={application.id}
              application={application}
              onApprove={handleApprove}
              onReject={handleReject}
              loading={processing[application.id]}
            />
          ))
        ) : (
          <div className="card text-center py-16">
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-full p-6 w-24 h-24 mx-auto mb-6">
              <Users className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">No Pending Applications</h3>
            <p className="text-gray-600 text-lg mb-6 max-w-md mx-auto">
              All tutor applications have been reviewed. Check back later for new submissions.
            </p>
            <div className="flex space-x-4 justify-center">
              <button
                onClick={handleManualRefresh}
                className="btn-primary px-6 py-3 text-lg"
              >
                Check for New Applications
              </button>
              {!socketConnected && (
                <button
                  onClick={handleReconnectSocket}
                  className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Wifi className="h-5 w-5" />
                  <span>Reconnect for Live Updates</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add some custom styles for animations */}
      <style jsx>{`
        @keyframes slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes scale-in {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </DashboardLayout>
  );
};

export default TutorApplications;